import { useMutation } from "@apollo/client";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { Search as SearchIcon } from "@tamagui/lucide-icons";
import { InsuranceStatus, webAgent__EndpointType } from "@medbillai/generated-graphql-types";
import { Body3, Input, Spinner, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import type { Endpoint } from "../../lib/accounts-link/types";
import { filterEndpoints } from "../../lib/accounts-link/utils";
import { type OnDone, tagged } from "../../lib/flow-engine";
import { sentryService } from "../../lib/sentry";
import { AccountLinkEndpoint } from "../accounts-link/components/AccountLinkEndpoint";
import { useWebAgentEndpointsQuery } from "../accounts-link/hooks/useWebAgentEndpointsQuery";
import type { OnboardingIdentity } from "./types";
import { recordInsuranceMutation, recordInsuranceStatusMutation, upsertOnboardingIntentMutation } from "./utils/queries";
export type InsuranceOutcomes = {
  selected: {
    carrier: string;
  };
  uninsured: {};
  skipped: {};
};
const Outcome = tagged<InsuranceOutcomes>();
const fuseOptions = {
  keys: ["displayName"],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  shouldSort: true
};

// No virtualization in the onboarding scroll region, so cap results.
const MAX_SEARCH_RESULTS = 30;

// Fade scrolled rows out at the top and bottom edges of the list region.
const LIST_EDGE_FADE_MASK = "linear-gradient(to bottom, transparent 0, black 20px, black calc(100% - 20px), transparent 100%)";
export function Insurance({
  identity,
  onDone
}: {
  /**
   * Absent when the run never passed through Profile — the carrier is then
   * recorded without a Member row.
   */
  identity?: OnboardingIdentity;
  onDone: OnDone<InsuranceOutcomes>;
}) {
  const [searchText, setSearchText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const {
    data,
    loading
  } = useWebAgentEndpointsQuery();
  const [recordInsurance] = useMutation(recordInsuranceMutation);
  const [recordInsuranceStatus] = useMutation(recordInsuranceStatusMutation);
  const [upsertSegmentation] = useMutation(upsertOnboardingIntentMutation);
  const {
    popularEndpoints,
    fetchedEndpoints
  } = useMemo(() => {
    // Carrier selection only — EHR portals are not insurance carriers.
    const insuranceEndpoints = (data?.webAgent__Endpoint ?? []).filter(endpoint => endpoint.type === webAgent__EndpointType.INSURANCE);
    return filterEndpoints(insuranceEndpoints);
  }, [data]);
  const fuse = useMemo(() => new Fuse(fetchedEndpoints, fuseOptions), [fetchedEndpoints]);
  const results = useMemo(() => {
    if (searchText.trim() === "") {
      return popularEndpoints;
    }
    return fuse.search(searchText, {
      limit: MAX_SEARCH_RESULTS
    }).map(result => result.item);
  }, [searchText, fuse, popularEndpoints]);

  // Carrier pick is the whole insurance step (no member ID): record, then
  // advance.
  const handleSelect = async (endpoint: Endpoint) => {
    const carrier = endpoint.displayName;
    if (!carrier || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setHasError(false);
    try {
      // `hasInsurance` (read by resume derivation) is the client's own write,
      // set only after the carrier record lands — flag-before-record would let
      // resume skip this screen with no carrier captured ("false complete").
      // The flag stays best-effort; its failure must not block the flow.
      await recordInsurance({
        variables: {
          input: {
            carrier,
            carrierSlug: endpoint.slug,
            ...identity
          }
        }
      });
      try {
        await upsertSegmentation({
          variables: {
            input: {
              hasInsurance: true
            }
          }
        });
      } catch (segmentationError) {
        console.warn("Failed to record segmentation hasInsurance", segmentationError);
      }
      onDone(Outcome.selected({
        carrier
      }));
    } catch (error) {
      sentryService.captureException(error);
      setHasError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // "I'm uninsured" is an answer, not a skip. Recording it lets later surfaces
  // — the financial-assistance application, a case checklist asking for a
  // member card — stop asking for a card this user does not have.
  const handleUninsured = async () => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    setHasError(false);
    try {
      await recordInsuranceStatus({
        variables: {
          input: {
            status: InsuranceStatus.UNINSURED
          }
        }
      });
    } catch (error) {
      // Advancing matters more than the record: a user who cannot get past
      // this screen is a worse outcome than one whose declaration we missed,
      // and the case checklist still offers its own way to say the same thing.
      sentryService.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
    onDone(Outcome.uninsured());
  };
  return <OnboardingScreen contained data-sentry-element="OnboardingScreen" data-sentry-component="Insurance" data-sentry-source-file="Insurance.tsx">
      <OnboardingScreen.Nav showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="Insurance.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.Header alignment="left" title="Select your insurance" subtitle="We'll alert you when there are opportunities to save on new bills." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="Insurance.tsx" />
        <YStack gap="$sp2" flex={1} $platform-web={{
        minHeight: 0
      }} data-sentry-element="YStack" data-sentry-source-file="Insurance.tsx">
          <Input testID="select-insurance.search" label="Search" leftIcon={SearchIcon} value={searchText} onChangeText={setSearchText} autoCapitalize="none" autoComplete="off" autoCorrect={false} spellCheck={false} data-sentry-element="Input" data-sentry-source-file="Insurance.tsx" />
          {loading ? <YStack alignItems="center" paddingVertical="$sp4">
              <Spinner />
            </YStack> :
        // Web: the contained screen gives this region shrink pressure, so
        // it scrolls internally and the footer stays on-screen; the mask
        // fades rows out at both scroll edges (the web analog of native's
        // AccountLinkListGradient overlay). Native keeps the screen-level
        // scroll region.
        <YStack $platform-web={{
          flexGrow: 1,
          flexShrink: 1,
          flexBasis: "auto",
          minHeight: 0,
          overflowY: "auto",
          maskImage: LIST_EDGE_FADE_MASK
        }}>
              {results.map(endpoint => <AccountLinkEndpoint key={endpoint.id} endpoint={endpoint} onClick={() => void handleSelect(endpoint)} />)}
              {results.length === 0 && searchText.trim() !== "" ? <Body3 color="$textSubtle" paddingVertical="$sp2">
                  No results — try a different name.
                </Body3> : null}
              {hasError ? <Body3 color="$textNegative" role="alert" aria-live="polite">
                  Something went wrong, please try again.
                </Body3> : null}
            </YStack>}
        </YStack>
      </OnboardingScreen.Content>
      <OnboardingScreen.Footer data-sentry-element="OnboardingScreen.Footer" data-sentry-source-file="Insurance.tsx">
        <OnboardingScreen.SecondaryAction testID="select-insurance.uninsured"
      // Gated while a carrier write is in flight: advancing as uninsured
      // mid-write would still land the carrier record.
      disabled={isSubmitting} onPress={() => void handleUninsured()} data-sentry-element="OnboardingScreen.SecondaryAction" data-sentry-source-file="Insurance.tsx">
          I'm uninsured
        </OnboardingScreen.SecondaryAction>
        <OnboardingScreen.TertiaryAction testID="select-insurance.skip"
      // Same in-flight gate as uninsured: a skip mid-write would still
      // land the carrier record ("false complete").
      disabled={isSubmitting} onPress={() => onDone(Outcome.skipped())} data-sentry-element="OnboardingScreen.TertiaryAction" data-sentry-source-file="Insurance.tsx">
          Skip
        </OnboardingScreen.TertiaryAction>
      </OnboardingScreen.Footer>
    </OnboardingScreen>;
}